import { MessageCircle, Ruler, Layers, Package2, ArrowDown } from "lucide-react";

const products = [
  {
    name: "Acrylic Sheet Bening",
    slug: "bening",
    desc: "Transparan jernih, ideal untuk display, signage, dan aplikasi yang menonjolkan kejernihan material.",
    swatch: "#F1F5F8",
    border: "border-neutral-200",
  },
  {
    name: "Acrylic Sheet Putih",
    slug: "putih",
    desc: "Putih susu (milky white) yang tembus cahaya — bukan putih solid. Sangat ideal untuk huruf timbul, panel interior, dan backlit signage.",
    swatch: "#FFFFFF",
    border: "border-neutral-200",
    note: "Putih Susu · Tembus Cahaya",
  },
  {
    name: "Acrylic Sheet Hitam",
    slug: "hitam",
    desc: "Hitam pekat dengan permukaan mengilap, memberi kesan elegan untuk furniture dan dekorasi.",
    swatch: "#111111",
    border: "border-neutral-800",
  },
  {
    name: "Acrylic Sheet Biru",
    slug: "biru",
    desc: "Biru solid untuk kebutuhan corporate signage, display, hingga aplikasi interior modern.",
    swatch: "#2563EB",
    border: "border-blue-200",
  },
  {
    name: "Acrylic Sheet Merah",
    slug: "merah",
    desc: "Merah terang dengan tampilan tajam untuk signage, huruf timbul, dan branding yang menonjol.",
    swatch: "#F5222D",
    border: "border-red-200",
  },
  {
    name: "Acrylic Sheet Kuning",
    slug: "kuning",
    desc: "Kuning cerah solid untuk display, dekorasi, dan aplikasi visual yang eye-catching.",
    swatch: "#FFD600",
    border: "border-yellow-200",
  },
];

const THICKNESSES = ["2mm", "3mm", "5mm"];
const SIZE = "122 × 244 cm";

export const Products = () => {
  return (
    <section id="produk" data-testid="products-section" className="py-24 lg:py-32 bg-animax-gray relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 relative">
        <div className="max-w-3xl">
          <div className="tag-uppercase text-animax-orange mb-4">Katalog Produk</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-animax-black">
            Pilihan ANIMAX Acrylic Sheet
          </h2>
          <p className="mt-4 text-neutral-600 text-lg leading-relaxed">
            Tersedia dalam 6 pilihan warna, 3 pilihan ketebalan, dan ukuran standar {SIZE} per lembar
            dengan harga kompetitif untuk kebutuhan retail, produksi, maupun proyek. Cocok untuk
            signage, display, interior, furniture, hingga aplikasi industri.
          </p>
        </div>

        {/* Catalog Spec Bar */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="catalog-spec-bar">
          <div className="p-5 rounded-xl bg-white border border-neutral-200 flex items-start gap-4">
            <div className="w-11 h-11 rounded-lg bg-animax-yellow/20 flex items-center justify-center shrink-0">
              <Package2 className="w-5 h-5 text-animax-orange" />
            </div>
            <div>
              <div className="tag-uppercase text-neutral-500">Pilihan Warna</div>
              <div className="mt-1 text-animax-black font-medium">6 Warna</div>
              <div className="text-xs text-neutral-500 mt-0.5">Bening · Putih · Hitam · Biru · Merah · Kuning</div>
            </div>
          </div>
          <div className="p-5 rounded-xl bg-white border border-neutral-200 flex items-start gap-4">
            <div className="w-11 h-11 rounded-lg bg-animax-yellow/20 flex items-center justify-center shrink-0">
              <Layers className="w-5 h-5 text-animax-orange" />
            </div>
            <div>
              <div className="tag-uppercase text-neutral-500">Ketebalan</div>
              <div className="mt-1 text-animax-black font-medium">2mm · 3mm · 5mm</div>
              <div className="text-xs text-neutral-500 mt-0.5">3 pilihan standar tersedia</div>
            </div>
          </div>
          <div className="p-5 rounded-xl bg-white border border-neutral-200 flex items-start gap-4">
            <div className="w-11 h-11 rounded-lg bg-animax-yellow/20 flex items-center justify-center shrink-0">
              <Ruler className="w-5 h-5 text-animax-orange" />
            </div>
            <div>
              <div className="tag-uppercase text-neutral-500">Ukuran Standar</div>
              <div className="mt-1 text-animax-black font-medium">{SIZE}</div>
              <div className="text-xs text-neutral-500 mt-0.5">Per lembar — ukuran standar</div>
            </div>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((p, i) => (
            <article
              key={p.slug}
              data-testid={`product-card-${p.slug}`}
              className={`card-lift group rounded-2xl overflow-hidden bg-white border ${p.border}`}
            >
              <div
                className="aspect-[5/4] relative overflow-hidden"
                style={{ background: p.swatch }}
              >
                <div className="absolute top-4 left-4 tag-uppercase text-white/90 mix-blend-difference">
                  0{i + 1} · Warna
                </div>
                <div className="absolute bottom-4 right-4 px-3 py-1.5 rounded-full glass-light">
                  <span className="text-[10px] tracking-widest uppercase font-medium text-animax-black">
                    UV Protected
                  </span>
                </div>
                {p.note && (
                  <div className="absolute bottom-4 left-4 px-3 py-1.5 rounded-full bg-animax-yellow" data-testid={`product-note-${p.slug}`}>
                    <span className="text-[10px] tracking-widest uppercase font-medium text-animax-black">
                      {p.note}
                    </span>
                  </div>
                )}
              </div>

              <div className="p-6">
                <h3 className="font-display text-xl font-medium text-animax-black">{p.name}</h3>
                <p className="mt-2 text-sm text-neutral-600 leading-relaxed min-h-[60px]">{p.desc}</p>

                <div className="mt-5 space-y-2.5">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-neutral-50 border border-neutral-200">
                    <div className="flex items-center gap-2">
                      <Layers className="w-3.5 h-3.5 text-animax-orange" />
                      <span className="tag-uppercase text-neutral-500">Ketebalan</span>
                    </div>
                    <div className="flex gap-1.5">
                      {THICKNESSES.map((t) => (
                        <span key={t} className="px-2 py-0.5 rounded-md bg-animax-black text-white text-[10px] font-medium">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-neutral-50 border border-neutral-200">
                    <div className="flex items-center gap-2">
                      <Ruler className="w-3.5 h-3.5 text-animax-orange" />
                      <span className="tag-uppercase text-neutral-500">Ukuran</span>
                    </div>
                    <span className="text-xs text-animax-black font-medium">{SIZE}</span>
                  </div>
                </div>

                <div className="mt-5">
                  <a
                    href="#tim-sales"
                    data-testid={`product-ask-${p.slug}`}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-animax-black text-white text-xs font-medium hover:bg-animax-orange transition-all"
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                    Tanya Harga & Stok
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-10 space-y-4">
          <div className="p-5 rounded-xl bg-white border border-dashed border-neutral-300 text-sm text-neutral-600 leading-relaxed" data-testid="products-note">
            <strong className="text-animax-black">Catatan:</strong> Seluruh produk tersedia dalam
            ukuran standar {SIZE} per lembar — saat ini kami belum melayani custom order/ukuran.
            Untuk informasi lebih detail, silakan hubungi kami langsung.
          </div>
          <div className="p-5 rounded-xl bg-white border border-dashed border-neutral-300 text-sm text-neutral-600 leading-relaxed" data-testid="products-color-disclaimer">
            <strong className="text-animax-black">Keterangan Warna:</strong> Warna yang ditampilkan
            pada katalog ini merupakan ilustrasi digital dan dapat berbeda dengan warna fisik produk
            aktual akibat perbedaan pengaturan layar dan pencahayaan. Untuk memastikan kesesuaian
            warna, silakan konsultasikan kebutuhan Anda dengan tim kami.
          </div>
        </div>

        <div className="mt-6 flex justify-center">
          <a
            href="#tim-sales"
            data-testid="products-to-sales"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-animax-yellow text-animax-black text-sm font-medium hover:bg-animax-orange hover:text-white transition-all"
          >
            Lihat Kontak Tim Sales Kami
            <ArrowDown className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};
